/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
/**
 * База знаний KIN DIGITAL CRM+ (встроена в программу, раздел «База знаний»).
 * Разметка статей: «## » заголовок, «- » список, «1. » нумерованный список, **жирный**, «> » примечание, пустая строка - новый абзац.
 */
export interface Article {
  id: string; title: string; section: string; summary: string; path?: string; module?: string; keywords?: string; body: string;
}

export const SECTIONS = ['Начало работы', 'Клиенты и продажи', 'Работа', 'Юридический контур', 'Банкротство', 'Документы и подпись', 'Клиенты онлайн', 'ИИ и автоматизация', 'Администрирование'];

export const ARTICLES: Article[] = [
  // ---------------- Начало работы ----------------
  {
    id: 'start', section: 'Начало работы', title: 'Как устроена CRM', path: '/',
    summary: 'Разделы, меню, права доступа и основные сущности: лицо, дело, документ, задача.',
    keywords: 'обзор меню вход начало',
    body: `KIN DIGITAL CRM+ - система для юридической фирмы и арбитражного управляющего: клиенты и продажи, дела и суды, банкротство, взыскание, документы с распознаванием и подписью, кабинет клиента, ИИ-помощник и автоматизации.

## Основные сущности
- **Лицо** - клиент, контрагент, должник, кредитор (организация, ИП или гражданин). У лица есть карточка с реквизитами, лентой событий, документами и делами.
- **Дело** - единица работы: судебный спор, банкротство, взыскание, исполнительное производство, проект или консультирование. К делу привязываются задачи, сроки, документы, судебные дела, время и затраты.
- **Документ** - файл с версиями. Сканы и фото распознаются, по тексту работает поиск. Документ можно подписать КЭП или отправить клиенту на подпись.
- **Задача** - поручение с исполнителем и сроком. Задачи ставят люди, автоматизации, мониторинг, онлайн-формы.

## Меню
- **Сводка и дашборды**: показатели фирмы и свои отчёты.
- **Работа**: задачи, календарь, дела, документы, поиск по смыслу, заказные письма, ИИ-ассистент, ИИ-агент, автоматизации.
- **Юридический контур**: процессуальные сроки, судебные дела, судебная практика, мониторинг, банкротство, торги, дебиторка, исполнительные листы, аналитика взыскания, время и счета.
- **Клиенты и продажи**: воронка, онлайн-формы, входящие, звонки, SMS, чат-бот, Мобильный Юрист, клиенты и контрагенты, договоры, затраты.
- **Администрирование**: пользователи и роли, шаблоны документов, справочники, ИИ и данные, фирменный стиль, лицензия, журнал аудита.

Пункты меню показываются по подключённым модулям лицензии и правам роли.

Пункты меню зависят от лицензии (какие модули подключены) и от прав вашей роли. Если раздела нет в меню, у вас нет к нему доступа.

## Лента событий
В карточках лица и дела есть «Лента»: всё, что происходило - звонки, заметки, документы, смена стадий, платежи, сообщения мониторинга и из кабинета клиента. Лента заполняется сама, вручную можно добавить заметку.

## Тур по программе
Кнопка «Тур по CRM» внизу меню проводит по всем разделам на демо-данных. Его можно пройти повторно в любой момент.

> Совет: начните с «Сводки», затем откройте любую карточку клиента и дело - так проще всего понять связи.`,
  },
  {
    id: 'install', section: 'Начало работы', title: 'Установка, запуск, обновление, настройки',
    summary: 'Переносная версия для Windows и серверная для Linux: запуск, перезапуск, ключи DaData и GigaChat, резервные копии.',
    keywords: 'установка start restart settings.env ключи перезапуск сервер linux ubuntu https',
    body: `Переносная версия для Windows работает без установки: в папке есть свой Node.js и PostgreSQL.

## Запуск
- Папка программы: **C:\\KIN DIGITAL\\KINDIGITAL-CRM**.
- Запуск: **START.cmd**, затем откройте в браузере http://localhost:3000.
- Перезапуск: **C:\\KIN DIGITAL\\RESTART.cmd**. Окно консоли CRM не закрывайте - в нём работает сервер.
- Если CRM открыта в браузере, а страница не отвечает, перезапустите программу.

## Настройки и ключи
Файл **data\\settings.env** внутри папки программы. Изменения подхватываются без перезапуска.
- DADATA_TOKEN - заполнение реквизитов по ИНН и мониторинг ЕГРЮЛ.
- AI_PROVIDER, GIGACHAT_AUTH_KEY, GIGACHAT_MODEL - ИИ (по умолчанию GigaChat-2-Max).
- Для GigaChat через Cloud.ru: AI_PROVIDER=openai, AI_BASE_URL=https://foundation-models.api.cloud.ru/v1, AI_API_KEY, AI_MODEL=GigaChat/GigaChat-2-Max.
- SMS_PROVIDER и ключи SMS.ru или SMSC - отправка кодов подписи по SMS. Для показа клиентам: SMS_PROVIDER=test и APP_DEMO_CODES=true (SMS не уходят, код входа в приложение виден на экране).
- MONITOR_INTERVAL_HOURS - как часто проверять мониторинг (по умолчанию 24 часа).

## Данные
- База данных и файлы хранятся в папке **data**. Для резервной копии достаточно скопировать папку data при остановленной программе.
- Распознавание речи использует модели в папке **models** и ffmpeg в папке **tools** - их не удаляйте.

## Доступ из интернета
Онлайн-формы, ссылки на подпись и кабинет клиента открываются у клиентов, только если CRM доступна из интернета (сервер или защищённый туннель). На ноутбуке они работают в локальной сети.

## Серверная версия (Linux)
- Установка на Ubuntu Server 24.04: распакуйте архив KINDIGITAL-CRM-server.tar.gz и выполните **sudo ./install.sh адрес-crm**. Установщик настроит HTTPS с бесплатным сертификатом, службу с автозапуском и ночные резервные копии.
- Программа и данные: **/opt/kindigital-crm**, настройки - **/opt/kindigital-crm/data/settings.env**, после правки: sudo systemctl restart kindigital-crm.
- Состояние и журнал: systemctl status kindigital-crm, journalctl -u kindigital-crm -f.
- Резервные копии: /opt/kindigital-crm/backups, каждую ночь, 14 дней.
- Обновление: распакуйте новую версию и снова выполните sudo ./install.sh - данные сохранятся.`,
  },
  {
    id: 'dashboard', section: 'Начало работы', title: 'Сводка', path: '/', module: 'M10',
    summary: 'Ключевые показатели, ближайшие заседания, события мониторинга, поступления и дебиторка.',
    keywords: 'дашборд показатели kpi отчёт рентабельность',
    body: `«Сводка» - главный экран руководителя.

## Что на экране
- **Показатели за период**: клиенты, открытые сделки, действующие договоры, дела в работе, просроченные задачи, поступило денег, затраты.
- **Ближайшие заседания** по всем судебным делам. Заседания в ближайшие 2 дня выделены красным.
- **Мониторинг: новые события** - непрочитанные изменения по ЕГРЮЛ и Федресурсу.
- **Поступления по месяцам** и **дебиторская задолженность клиентов** по договорам.

## Действия
- Период задаётся полями «С» и «По».
- «Рентабельность в Excel» выгружает доходы и затраты по делам и договорам.`,
  },

  // ---------------- Клиенты и продажи ----------------
  {
    id: 'parties', section: 'Клиенты и продажи', title: 'Клиенты и контрагенты', path: '/parties', module: 'M01',
    summary: 'Карточка лица, заполнение по ИНН, связи, счета, согласия, импорт, дубли и объединение.',
    keywords: 'клиент контрагент ИНН DaData карточка импорт дубли объединение согласие',
    body: `Единый справочник всех лиц: клиенты, контрагенты, должники, кредиторы.

## Создание
- «Добавить», выберите вид: организация, ИП или гражданин.
- Для организации и ИП введите ИНН и нажмите поиск: наименование, КПП, ОГРН, адрес и руководитель подтянутся из ЕГРЮЛ (нужен ключ DaData).
- Отметки «Клиент» и «Контрагент» влияют на фильтры и отчёты.

## Карточка лица
- **Сведения**: реквизиты, телефоны, почта, источник, связанные лица (учредитель, руководитель, поручитель и т.п.), банковские счета, согласия на обработку данных и рассылки.
- **Лента**: все события по лицу.
- **Договоры, Дела, Задачи, Документы** - всё, что связано с лицом.
- **Финанализ** (для организаций и ИП) - анализ по ПП № 367, см. отдельную статью.
- Внизу вкладки «Сведения»: блоки **Мониторинг ЕГРЮЛ и Федресурса** и **Личный кабинет**.

## Импорт и дубли
- Импорт из CSV: колонки наименование, ИНН, телефон, почта и т.д.; можно сразу отметить всех клиентами и поставить метку.
- CRM подсказывает возможные дубли по ИНН, телефону, почте или точному названию. Дубли объединяются: договоры, дела, документы и лента переносятся в основную карточку.`,
  },
  {
    id: 'deals', section: 'Клиенты и продажи', title: 'Воронка продаж', path: '/deals', module: 'M02',
    summary: 'Канбан сделок, стадии, причины отказа, конвертация в клиента и дело, аналитика.',
    keywords: 'сделка лид воронка стадия продажи канбан',
    body: `Воронка показывает обращения и сделки по стадиям.

## Работа со сделкой
- Новая сделка: название, контакт, телефон, почта, услуга, сумма, ответственный, источник.
- Перетащите карточку на другую стадию. При переходе в стадию отказа CRM попросит указать причину.
- Смена стадии пишется в ленту и может запускать автоматизации (событие «Сделка перешла на стадию»).
- «Конвертировать» создаёт карточку клиента (или находит существующую) и привязывает к ней сделку.

## Откуда приходят сделки
- Вручную.
- Из онлайн-формы «Заявка на консультацию» и других форм, настроенных на создание сделки.

## Настройка
Воронок может быть несколько, стадии и их порядок настраиваются; у стадии можно задать срок (SLA) в часах и отметки «выиграна» или «проиграна». Аналитика показывает конверсию и причины отказов за период.`,
  },
  {
    id: 'forms', section: 'Клиенты и продажи', title: 'Онлайн-формы и анкеты', path: '/forms', module: 'M38',
    summary: 'Конструктор анкет, готовая анкета должника БФЛ, публичная ссылка, автоматическое создание клиента, сделки или дела.',
    keywords: 'анкета форма сайт заявка бфл кредитор iframe',
    body: `Онлайн-формы принимают обращения с сайта или по ссылке, которую вы отправляете клиенту.

## Готовые анкеты
- **Анкета должника (банкротство гражданина)**: личные данные, семья, долги и кредиторы, доходы, имущество, сделки за 3 года, загрузка документов, список нужных документов. Создаёт клиента и дело «Банкротство».
- **Заявка на консультацию**: короткая форма для сайта, создаёт сделку в воронке.
- **Обращение кредитора к арбитражному управляющему**: требование и документы кредитора.

## Свой конструктор
- Поля: строка, текст, телефон, почта, ИНН, дата, число, выбор из списка, флажок, файлы, заголовок раздела.
- Порядок полей меняется стрелками, поле можно сделать обязательным.
- **Ключи** fullName, phone, email, inn, birthDate, address заполняют карточку клиента.
- «Что создаётся»: сделка в воронке или клиент и дело выбранного вида.
- Свои тексты: пояснение, список документов, сообщение после отправки.

## Что происходит после отправки
1. CRM проверяет обязательные поля, телефон, почту и ИНН.
2. Ищет клиента по ИНН, почте или телефону; если не нашла - создаёт.
3. Создаёт сделку или дело, сохраняет файлы в документы и запускает их распознавание.
4. Пишет ответы в ленту и ставит ответственному задачу «Новая заявка».
5. Запускает автоматизации (событие «Заполнена онлайн-форма»).

## Размещение на сайте
Ссылка вида /f/адрес-формы. На сайт ставится кнопкой со ссылкой или кодом iframe (показан внизу страницы). Защита от спама: скрытое поле-ловушка и ограничение 20 отправок в час с одного адреса.`,
  },
  {
    id: 'contracts', section: 'Клиенты и продажи', title: 'Договоры, затраты и расходы', path: '/contracts', module: 'M03',
    summary: 'Договоры с этапами и платежами, финансы договора, затраты по делам и их согласование.',
    keywords: 'договор этап платеж оплата затраты расходы согласование рентабельность',
    body: `## Договоры
- Договор привязан к клиенту, может иметь этапы (с суммами и сроками) и платежи (план и факт).
- Финансы договора: сумма, оплачено, остаток, затраты и рентабельность.
- Поступивший платёж пишется в ленту и запускает автоматизации (событие «Поступил платёж по договору»).
- Фильтр «истекающие» показывает договоры, срок которых скоро заканчивается.

## Затраты и расходы
- **Производственные затраты** - по конкретному делу или договору (госпошлина, почта, выезд, услуги подрядчика).
- **Расходы** - общие расходы фирмы.
- Затраты можно согласовывать (статусы черновик, согласовано, отклонено) и отмечать как возмещаемые клиентом.
- Сводка затрат по категориям за период; затраты учитываются в рентабельности на «Сводке».`,
  },

  // ---------------- Работа ----------------
  {
    id: 'tasks', section: 'Работа', title: 'Задачи и календарь', path: '/tasks', module: 'M08',
    summary: 'Задачи с приоритетом и сроком, чек-листы, повторяющиеся задачи, комментарии, календарь встреч и заседаний.',
    keywords: 'задача поручение календарь встреча заседание повторяющаяся чек-лист',
    body: `## Задачи
- У задачи есть исполнитель, срок, приоритет, описание, чек-лист и наблюдатели; её можно привязать к лицу, делу, договору или сделке.
- Статусы: к выполнению, в работе, выполнена, отменена. Просроченные задачи видны на «Сводке».
- **Повторяющиеся задачи** (ежедневно, еженедельно, ежемесячно): при выполнении создаётся следующая.
- Комментарии к задаче видны всей команде.
- Задачи создаются и автоматически: автоматизациями, мониторингом, онлайн-формами, кабинетом клиента, подписанием документов.

## Календарь
- Встречи, заседания, сроки и прочие события по дням, неделям и месяцам.
- Назначенное заседание в «Судебных делах» само попадает в календарь.
- Событие можно привязать к делу и лицу.`,
  },
  {
    id: 'matters', section: 'Работа', title: 'Дела', path: '/matters',
    summary: 'Карточка дела и все её вкладки: задачи, суд, сроки, процедура, лента, документы, затраты, время, ИИ, команда.',
    keywords: 'дело карточка команда конфиденциально вкладки',
    body: `Дело - центральная карточка работы юриста.

## Виды дел
Судебное дело, банкротство, исполнительное производство, взыскание, проект, консультирование. От вида зависят доступные вкладки.

## Вкладки карточки
- **Задачи** - поручения по делу.
- **Суд** - номера судебных дел и ближайшие заседания (см. «Судебные дела»).
- **Сроки** - процессуальные сроки по производственному календарю.
- **Процедура** - для банкротства: процедура, реестр требований, чек-лист АУ.
- **Лента** - история дела.
- **Документы** - с версиями, распознаванием, КЭП и подписью клиента.
- **Затраты** и **Время** - учёт расходов и часов по делу.
- **ИИ** - вопросы ИИ-ассистенту с учётом материалов дела.
- **Команда** - сотрудники дела.

## Доступ
Дело можно отметить конфиденциальным: его видят только участники команды и руководство. Такие дела и их документы не показываются в кабинете клиента.`,
  },
  {
    id: 'documents', section: 'Документы и подпись', title: 'Документы: версии, распознавание, поиск', path: '/documents', module: 'M09',
    summary: 'Загрузка, версии, автоматическое распознавание сканов и фото, поиск по тексту, извлечение реквизитов.',
    keywords: 'документ скан ocr распознавание версия поиск реквизиты pdf фото',
    body: `## Загрузка и версии
- «Загрузить» - один или несколько файлов, с привязкой к делу, лицу или договору, видом, папкой и метками.
- «Новая версия» сохраняет предыдущие; скачать можно любую. Для каждого файла считается контрольная сумма SHA-256.
- Имя при скачивании формируется по правилу «дата - вид - описание».

## Распознавание (M29)
- Сканы, фото и PDF распознаются сами в фоне (русский язык). У PDF с текстовым слоем текст берётся точно.
- Значок в строке показывает статус и качество распознавания. Кнопка «Текст и реквизиты» открывает распознанный текст.
- Если качество низкое (фото, муар, рукописные пометки), нажмите «Распознать через GigaChat»: изображение отправляется в облако Сбера.
- «Извлечь реквизиты» находит в тексте стороны, суммы, даты, номера дел и т.п.
- По распознанному тексту работает поиск в разделе «Документы».

## Значки в строке документа
- **Глаз** - показать документ в кабинете клиента или скрыть.
- **Щит** - подписать КЭП (КриптоПро). Зелёный щит - документ подписан.
- **Перо** - отправить клиенту на подпись простой ЭП по коду.
- **Скачать** и «Новая версия».`,
  },
  {
    id: 'asr', section: 'Документы и подпись', title: 'Расшифровка звонков и заседаний', path: '/documents', module: 'M30',
    summary: 'Запись звонка или заседания превращается в текст с отметками времени прямо на компьютере, плюс резюме и задачи через ИИ.',
    keywords: 'аудио звонок запись речь расшифровка mp3 резюме',
    body: `Загрузите в «Документы» аудио или видео (mp3, m4a, ogg из мессенджеров, wav, mp4) - CRM расшифрует его сама.

## Как это работает
- Распознавание идёт **на этом компьютере** (модель GigaAM, Сбер): запись никуда не отправляется.
- Текст разбит на фрагменты с отметками времени: [мм:сс] текст.
- Скорость: несколько минут записи обрабатываются за 1-2 минуты.

## Резюме и задачи
В окне «Текст и реквизиты» у аудио есть кнопка «Резюме и задачи»: ИИ готовит краткое содержание разговора и список поручений. Задачи можно создать одной кнопкой, по делу, если документ к нему привязан. Для резюме текст отправляется в GigaChat.

> Ограничение: расшифровка без знаков препинания и заглавных букв, имена собственные иногда искажаются.`,
  },
  {
    id: 'kep', section: 'Документы и подпись', title: 'Подпись КЭП (КриптоПро)', path: '/documents', module: 'M13',
    summary: 'Подписание документа квалифицированной подписью через КриптоПро ЭЦП Browser plug-in, файл .sig, удаление ошибочной подписи.',
    keywords: 'кэп эцп криптопро подпись sig токен рутокен сертификат',
    body: `## Что нужно
- КриптоПро CSP и «КриптоПро ЭЦП Browser plug-in» на компьютере.
- Расширение CryptoPro в браузере (Chrome, Яндекс.Браузер, Edge).
- Действующий сертификат (на токене или в реестре).

## Как подписать
1. В «Документах» нажмите **щит** в строке документа.
2. При первом обращении КриптоПро спросит разрешение для сайта - нажмите «Да».
3. Выберите сертификат и нажмите «Подписать». Токен может запросить PIN.
4. Подпись сохраняется в CRM рядом с версией файла; документ получает метку «подписан КЭП».

## Что получается
- Отсоединённая подпись **CAdES-BES** (файл .sig) по ГОСТ Р 34.11-2012 (256 или 512 бит - по ключу сертификата).
- В карточке подписи: подписант, должность, ИНН, ОГРН, СНИЛС, срок действия сертификата, версия документа.
- Проверить подпись можно на Госуслугах (сервис проверки ЭП) или в КриптоАРМ: нужны сам документ и файл .sig.

## Правила
- Одним сертификатом версию документа можно подписать **только один раз**. Чтобы подписать заново, загрузите новую версию или удалите прежнюю подпись.
- Удалить подпись может подписавший сотрудник или администратор (кнопка «Удалить»). Удаление записывается в журнал аудита и ленту.`,
  },
  {
    id: 'pep', section: 'Документы и подпись', title: 'Подписание клиентом (простая ЭП по коду)', path: '/documents', module: 'M39',
    summary: 'Отправка документа клиенту на подпись по ссылке, соглашение о ПЭП, одноразовый код, несколько подписантов, лист подписания.',
    keywords: 'пэп простая подпись клиент код sms подписание лист подписания 63-фз',
    body: `Клиент подписывает документ с телефона или компьютера простой электронной подписью (ст. 6, 9 63-ФЗ).

## Отправка на подпись
1. В «Документах» нажмите **перо** в строке документа.
2. Добавьте подписантов: клиента из базы (телефон подставится) или ФИО и телефон вручную. Подписантов может быть несколько.
3. Добавьте сообщение и нажмите «Отправить на подпись».
4. У каждого подписанта своя ссылка. Если SMS-провайдер подключён, ссылка уходит по SMS; если нет - скопируйте её и отправьте в мессенджер.

## Что видит клиент
1. Документ (PDF открывается прямо на странице) и его контрольную сумму.
2. Текст соглашения об использовании простой ЭП - его нужно принять.
3. Кнопку «Получить код». Код из 6 цифр действует 15 минут, не более 5 попыток ввода и 5 отправок.
4. После ввода кода документ подписан. Можно отказаться от подписания с указанием причины.

## Код без SMS
Пока SMS-провайдер не подключён, запрошенный клиентом код появляется у сотрудника в окне подписания («Код для клиента») - его сообщают по телефону. В листе подписания это отмечается как «код сообщён сотрудником».

## Результат
- Когда подписали все, CRM создаёт **лист подписания** (DOCX) рядом с документом: файл, SHA-256, время открытия ссылки, принятия соглашения, отправки кода и подписи, IP-адрес и браузер каждого подписанта.
- Документ получает метку «подписан ПЭП», ответственному ставится задача, срабатывает событие «Клиент подписал документ».
- При отказе ставится задача с причиной.

## Подключение SMS
В data\\settings.env: SMS_PROVIDER=smsru и SMSRU_API_ID, или SMS_PROVIDER=smsc, SMSC_LOGIN и SMSC_PASSWORD. Имя отправителя - SMS_SENDER.`,
  },
  {
    id: 'ezp', section: 'Документы и подпись', title: 'Заказные письма (ЭЗП)', path: '/ezp', module: 'M13',
    summary: 'Архив для массовой отправки на zakaznoe.pochta.ru: проверка писем, реестр в официальном шаблоне Почты, опись.',
    keywords: 'эзп почта заказное письмо реестр массовая отправка опись zakaznoe',
    body: `## Подготовка
- Письма загрузите в «Документы» в PDF (формат А4, не более 19 страниц).
- У получателя-организации должны быть наименование, ИНН (10 цифр) и КПП (9 цифр); у гражданина или ИП - фамилия и имя. Адрес с индексом берётся из почтового или юридического адреса карточки, его можно поправить.

## Сборка отправки
1. «Заказные письма» → «Новая отправка».
2. Укажите название, адрес отправителя (запоминается), вид писем (заказные или простые).
3. Найдите документы по названию и для каждого выберите получателя.
4. «Проверить»: CRM проверит PDF, формат и страницы, реквизиты получателя, индекс.
5. «Собрать архив»: архив скачается и сохранится в документах вместе с описью.

## Что внутри архива
- Письма с именами латиницей (01_..., 02_...).
- **registry.xlsx** - реестр в **официальном шаблоне Почты**. CRM заполняет только строки данных, шапку, лист «Инструкция» и служебные столбцы не трогает - иначе Почта отвечает «Некорректный хедер в реестре».
- Не более 50 писем в одном архиве.

## Отправка
zakaznoe.pochta.ru → «Новое письмо» → «Массовая отправка» → загрузить архив → проверить → при необходимости подписать КЭП → «Оплатить и отправить». Затем отметьте отправку в CRM как отправленную.`,
  },
  {
    id: 'templates', section: 'Документы и подпись', title: 'Шаблоны документов', path: '/admin/templates', module: 'M36',
    summary: 'Формирование документов DOCX из шаблонов с подстановкой данных дела, долга, процедуры.',
    keywords: 'шаблон docx генерация претензия запрос уведомление поля',
    body: `## Встроенные шаблоны
- Претензия о погашении задолженности (взыскание).
- Запрос в банк о счетах и движении средств должника (банкротство).
- Уведомление кредитора о введении процедуры (банкротство).

## Свои шаблоны
- Подготовьте DOCX в Word и вставьте поля в фигурных скобках, например {debtor.name}, {case.number}, {amount}.
- «Список полей» показывает доступные поля для каждой сущности (долг, процедура, дело, лицо).
- Загрузите шаблон и укажите, к чему он относится. В карточках долга, процедуры и дела появится кнопка формирования документа.
- Готовый документ сразу сохраняется в «Документы» дела.

> Правило оформления: блок подписи не должен оставаться один на последней странице - оставляйте перед ним 3-4 строки текста.`,
  },

  // ---------------- Юридический контур ----------------
  {
    id: 'deadlines', section: 'Юридический контур', title: 'Процессуальные сроки', path: '/deadlines', module: 'M40',
    summary: 'Калькулятор сроков по АПК, ГПК, 127-ФЗ, 229-ФЗ с производственным календарём, контроль и задачи.',
    keywords: 'срок апелляция кассация жалоба давность календарь рабочие дни',
    body: `## Как считается срок
- Рабочие дни - по производственному календарю РФ (2024-2027, с переносами праздников). Администратор может поправить отдельные дни.
- Сроки в днях по АПК, ГПК и 229-ФЗ считаются в рабочих днях; сроки в месяцах и годах - календарно, с переносом окончания на ближайший рабочий день.
- Если год ещё не внесён в календарь, срок помечается как приблизительный.

## Готовые сроки
- Апелляция (АПК ст. 259, ГПК ст. 321), жалоба на определение (АПК ст. 188, 223), кассация (АПК ст. 276, ГПК ст. 376.1), жалоба в ВС РФ (АПК ст. 291.2), частная жалоба (ГПК ст. 332).
- Банкротство: требование в наблюдении (ст. 71), закрытие реестра в конкурсе (ст. 142) и у гражданина (ст. 213.24), жалоба на определение (ст. 61).
- Жалоба на пристава (229-ФЗ ст. 15, 122), исковая давность (ГК ст. 196), претензионный порядок (АПК ст. 4).

## Контроль
- Срок привязывается к делу и ответственному, по нему создаётся задача.
- Раз в сутки CRM проверяет сроки на 5 рабочих дней вперёд и запускает событие «Подходит процессуальный срок» - на него можно повесить эскалацию в «Автоматизациях».`,
  },
  {
    id: 'courtcases', section: 'Юридический контур', title: 'Судебные дела и КАД', path: '/court-cases', module: 'M12',
    summary: 'Реестр судебных дел, хронология, заседания в календаре, перенос событий из Картотеки арбитражных дел.',
    keywords: 'суд кад заседание картотека хронология tampermonkey',
    body: `## Реестр
- Номер дела, суд, судья, роль (истец, ответчик, кредитор, АУ...), предмет, лицо и дело CRM.
- Ближайшие заседания видны в списке, на «Сводке» и в кабинете клиента.
- Дела о банкротстве добавляются сами из мониторинга Федресурса.

## Карточка
- Хронология: заседания, судебные акты, документы сторон. События добавляются вручную или из КАД.
- «Назначить» заседание: дата, время, зал или ВКС. Заседание попадает в календарь и ленту дела, срабатывает событие «Назначено судебное заседание».
- Кнопка «Карточка в КАД» или «Найти в КАД».

## Перенос из КАД
Картотека блокирует программы, поэтому данные переносит скрипт в вашем браузере.
1. Установите в Chrome расширение Tampermonkey.
2. «Судебные дела» → «Связь с КАД» → «Скачать скрипт» и импортируйте его в Tampermonkey.
3. Откройте карточку дела на kad.arbitr.ru и нажмите кнопку **«В CRM»** справа внизу.
4. В CRM попадут события хронологии, ссылки на PDF судебных актов и дата ближайшего заседания. Повторное нажатие добавляет только новое.

Новые акты из КАД запускают событие «Новое событие в судебном деле» - например, правило «решение → срок на апелляцию».`,
  },
  {
    id: 'monitoring', section: 'Юридический контур', title: 'Мониторинг ЕГРЮЛ и Федресурса', path: '/monitoring', module: 'M41',
    summary: 'Ежедневная проверка контрагентов и должников: статус, руководитель, адрес, дела о банкротстве, сообщения ЕФРСБ и ЕФРСДЮЛ.',
    keywords: 'мониторинг егрюл федресурс ефрсб ликвидация банкротство директор контрагент',
    body: `## Что отслеживается
- **ЕГРЮЛ/ЕГРИП** (через DaData): статус (действует, ликвидируется, ликвидирована, банкротство, реорганизация), наименование, адрес, руководитель, основной ОКВЭД.
- **Федресурс**: новые дела о банкротстве, смена процедуры, новые сообщения ЕФРСБ и ЕФРСДЮЛ (с ссылкой на сообщение).

## Как включить
- «Мониторинг» → «Добавить на мониторинг» → выберите лица (нужен ИНН), или кнопка «Включить» в карточке лица.
- Первая проверка запоминает текущее состояние; о дальнейших изменениях CRM сообщает.

## Что происходит при изменении
- Событие в разделе «Мониторинг» и в ленте лица; непрочитанные видны на «Сводке».
- По важным событиям (банкротство, ликвидация, смена директора или адреса, введение процедуры, торги, собрания) ставится задача ответственному.
- Найденное дело о банкротстве добавляется в «Судебные дела».
- Срабатывает событие «Событие мониторинга» для автоматизаций.

## Режим проверок
Раз в сутки по каждому лицу, «Проверить сейчас» - вручную. К Федресурсу CRM обращается с паузами, чтобы не получить блокировку; ошибки показываются в списке и не сбрасывают уже известные сообщения.`,
  },
  {
    id: 'debts', section: 'Юридический контур', title: 'Дебиторская задолженность', path: '/debts', module: 'M11',
    summary: 'Портфель долгов, этапы взыскания, проценты по ст. 395 ГК, мораторий, неустойка, исковая давность, импорт цессии.',
    keywords: 'долг взыскание 395 проценты неустойка давность цессия мораторий',
    body: `## Долг
- Должник, кредитор, основание, сумма, дата возникновения, этап взыскания: досудебный, претензия, суд, ИЛ, исполнение, банкротство, погашен, списан.
- Оплаты по долгу уменьшают остаток.

## Расчёты
- **Проценты по ст. 395 ГК** по ключевой ставке Банка России с учётом частичных оплат.
- **Мораторий** на финансовые санкции учитывается автоматически.
- **Договорная неустойка** по ставке в день.
- **Исковая давность**: дата окончания с учётом признания долга; истекающие выделяются.
- Расчёт выгружается в Excel для приложения к иску.

## Портфель
- Импорт долгов из файла (например, реестр цессии): должники создаются или находятся по ИНН.
- По долгу формируется претензия из шаблона.`,
  },
  {
    id: 'writs', section: 'Юридический контур', title: 'Исполнительные листы', path: '/writs', module: 'M17',
    summary: 'ИЛ, судебные приказы, предъявление в ФССП или банк, ИП, поступления, срок предъявления.',
    keywords: 'исполнительный лист фссп пристав ип банк взыскание',
    body: `- Вид документа (ИЛ, судебный приказ, исполнительная надпись), номер, кем и когда выдан, номер дела, должник, сумма.
- Предъявление: отдел ФССП или банк, дата, номер ИП, пристав. Статусы: получен, в ФССП, в банке, в работе, исполнен, возвращён, окончен.
- Поступления по ИЛ: остаток считается сам.
- **Срок предъявления** (ст. 21 229-ФЗ) - по умолчанию 3 года от даты выдачи; истекающие выделяются красным.`,
  },
  {
    id: 'time', section: 'Юридический контур', title: 'Учёт времени и счета', path: '/time', module: 'M35',
    summary: 'Таймер и записи времени по делам, ставки, выставление счетов клиентам, счета в кабинете клиента.',
    keywords: 'время часы таймер счёт биллинг ставка',
    body: `- **Таймер**: запуск и остановка по делу, время записывается само.
- Записи вручную: дата, длительность, описание, оплачиваемая или нет.
- **Счёт клиенту**: собирается из записей времени и затрат, номер и дата присваиваются автоматически. Статусы: черновик, выставлен, оплачен, отменён.
- Выставленные и оплаченные счета клиент видит в личном кабинете.
- Сводка часов по сотрудникам и делам - в отчётах.`,
  },

  // ---------------- Банкротство ----------------
  {
    id: 'bankruptcy', section: 'Банкротство', title: 'Процедуры банкротства: рабочее место АУ', path: '/bankruptcy', module: 'M14',
    summary: 'Процедура, чек-лист обязательных действий, реестр требований по очередям, голоса, распределение, вознаграждение.',
    keywords: 'банкротство процедура реестр требований очередь голоса распределение вознаграждение чек-лист ау',
    body: `## Процедура
- Вид: наблюдение, финансовое оздоровление, внешнее управление, конкурсное производство, реструктуризация, реализация имущества, мировое соглашение.
- Номер дела, суд, судья, управляющий и СРО, дата введения, даты публикаций в ЕФРСБ и «Коммерсанте», ближайшее заседание, фиксированное вознаграждение.
- Даты публикаций вводятся вручную - программа их не подставляет.

## Чек-лист
Обязательные действия по 127-ФЗ со сроками от даты введения: публикации, уведомления, инвентаризация, закрытие реестра, собрания, положение о продаже и т.п. Просроченные пункты выделены. Сроки - типовые ориентиры, сверяйте их с законом и актом суда.

## Реестр требований
- Кредитор, очередь (1, 2, 3, залоговые, за реестром, текущие), основной долг, санкции (учитываются отдельно, п. 3 ст. 137), залог, основание, судебный акт, дата включения, статус, погашение.
- Проценты голосов на собрании считаются сами.
- Распределение конкурсной массы по очередям и пропорционально внутри очереди.
- Расчёт вознаграждения управляющего.

## Документы по процедуре
Из шаблонов формируются запросы в банки и уведомления кредиторов. Анализ финансового состояния - во вкладке «Финанализ» карточки должника.`,
  },
  {
    id: 'auctions', section: 'Банкротство', title: 'Торги', path: '/auctions', module: 'M16',
    summary: 'Лоты, этапы торгов, расчёт сроков по производственному календарю, публичное предложение, задаток и шаг.',
    keywords: 'торги лот аукцион публичное предложение задаток шаг 110 139',
    body: `## Правила расчёта сроков
- Вы вводите даты публикации в «Коммерсанте» и ЕФРСБ (дата ЕФРСБ указана на ЭТП). Сроки считаются от более поздней.
- **Приём заявок - 26 рабочих дней** (закон требует не менее 25, берётся запас в 1 день) по производственному календарю.
- Торги не ранее 30 дней после публикации (п. 9 ст. 110).
- Публикация следующих торгов - не ранее чем через неделю после предыдущих.

## Лот и этапы
- Лот: описание, начальная цена, задаток и шаг в процентах, процедура.
- Этапы: первые торги, повторные (цена на 10% ниже), публичное предложение.
- **График публичного предложения**: периоды снижения цены (в днях или рабочих днях, первый период может быть длиннее), процент снижения, цена отсечения.

> Комплект документов по торгам: проект договора купли-продажи и договор о задатке (при задатке на счёт ЭТП - шаблон площадки).`,
  },
  {
    id: 'finanalysis', section: 'Банкротство', title: 'Анализ финансового состояния', path: '/parties', module: 'M15',
    summary: 'Коэффициенты по ПП № 367 за несколько лет, загрузка отчётности из ГИР БО, выводы, выгрузка в Excel.',
    keywords: 'финанализ 367 коэффициенты баланс гир бо отчётность ликвидность',
    body: `Вкладка «Финанализ» в карточке организации или ИП.

## Данные
- **«Загрузить отчётность из ГИР БО»** - бухгалтерская отчётность по ИНН из государственного ресурса bo.nalog.gov.ru за все доступные годы.
- **«Ввести год вручную»** - строки баланса и отчёта о финансовых результатах (коды строк 1100-2400) и просроченная кредиторская задолженность.

## Коэффициенты (ПП РФ № 367)
- Платёжеспособность: абсолютная и текущая ликвидность, обеспеченность обязательств активами, степень платёжеспособности по текущим обязательствам.
- Финансовая устойчивость: автономия, обеспеченность собственными оборотными средствами, доля просроченной кредиторской задолженности, отношение дебиторской задолженности к активам.
- Деловая активность: рентабельность активов, норма чистой прибыли.

Значения вне нормы выделены красным, в норме - зелёным. Для каждого коэффициента показана формула по строкам отчётности.

## Выводы
CRM готовит черновик выводов: какие показатели вне нормы, отрицательные чистые активы, падение выручки и активов, рост кредиторской задолженности. Окончательные выводы делает арбитражный управляющий. «Анализ в Excel» выгружает таблицу для заключения.`,
  },

  // ---------------- Клиенты онлайн ----------------
  {
    id: 'portal', section: 'Клиенты онлайн', title: 'Личный кабинет клиента и кредитора', path: '/parties', module: 'M18',
    summary: 'Вход по личной ссылке, дела и заседания, открытые документы, запросы документов, переписка, подпись, счета, требования кредитора.',
    keywords: 'кабинет портал клиент кредитор ссылка переписка запрос документов',
    body: `## Как открыть кабинет
1. Карточка клиента → вкладка «Сведения» → блок **Личный кабинет**.
2. «Открыть кабинет клиента» (или «Кабинет кредитора») - появится личная ссылка вида /p/....
3. Скопируйте ссылку и отправьте клиенту. Пароль не нужен; ссылка личная.
- «Новая ссылка» делает прежнюю недействительной, «закрыть доступ» отключает кабинет.
- Видно, когда клиент входил последний раз.

## Что видит клиент
- **Нужно от вас**: ваши запросы документов (с кнопкой загрузки файла) и документы на подпись.
- **Мои дела**: дело, вид, статус, номера судебных дел и ближайшие заседания (конфиденциальные дела не показываются).
- **Документы**: только те, что вы открыли значком «глаз» в списке документов, и присланные самим клиентом.
- **Сообщения**: переписка с юристом.
- **Счета**: выставленные и оплаченные.

## Кабинет кредитора
Кредитор видит свои требования в реестрах: должник, дело, сумма, очередь, залог, статус включения, погашения, управляющий, ближайшее заседание.

## Что получает юрист
- Загруженный клиентом файл сохраняется в документы (с распознаванием), запрос закрывается, ставится задача.
- Сообщения клиента видны в блоке «Личный кабинет», в ленте и срабатывает событие «Сообщение клиента в личном кабинете».`,
  },

  // ---------------- ИИ и автоматизация ----------------
  {
    id: 'ai', section: 'ИИ и автоматизация', title: 'ИИ-ассистент', path: '/assistant', module: 'M20',
    summary: 'Вопросы GigaChat по праву и по материалам дела, распознавание сложных сканов, резюме звонков, извлечение реквизитов.',
    keywords: 'ии gigachat ассистент нейросеть вопрос дело',
    body: `## Где используется ИИ
- **ИИ-ассистент** (меню «Работа»): вопросы на любые юридические темы. Ctrl+Enter - отправить.
- **Вкладка «ИИ» в деле**: ассистент получает сведения о деле (стороны, процедура, сроки, документы) и отвечает с их учётом.
- **Документы**: «Распознать через GigaChat» для плохих сканов, «Извлечь реквизиты», «Резюме и задачи» для звонков.
- **Автоматизации**: действие «ИИ-шаг» готовит текст по событию и записывает его в ленту.

## Модель
Поддерживаются GigaChat (по договору лицензиата со Сбером или Cloud.ru), YandexGPT и локальные модели на вашем сервере (LM Studio, Ollama) - выбор в data\\settings.env.

## Важно
- ИИ только предлагает: решения и отправку документов делает человек.
- Ответы о сроках и нормах проверяйте по тексту закона: модель может ошибаться.
- Передачу данных во внешнюю модель разрешает администратор (Администрирование → ИИ и данные). В режиме «Только локально» данные не покидают ваш сервер.`,
  },
  {
    id: 'automations', section: 'ИИ и автоматизация', title: 'Автоматизации и шаблоны процессов', path: '/automations', module: 'M37',
    summary: 'Правила «если - то»: события, условия, действия, подстановки, шаблоны процессов, журнал и пробный запуск.',
    keywords: 'автоматизация правило триггер событие условие действие вебхук процесс шаблон',
    body: `## Из чего состоит правило
- **Когда (событие)**: открыто дело; изменён статус дела; сделка перешла на стадию; выполнена задача; поступил платёж; загружен документ; новое событие в судебном деле; назначено заседание; событие мониторинга; заполнена онлайн-форма; клиент подписал документ; сообщение клиента в кабинете; подходит процессуальный срок.
- **Условия** (все должны выполняться): поле события равно, не равно, содержит, не содержит, больше, меньше, пусто, не пусто. Например: вид дела = «Взыскание»; текст события содержит «Решение».
- **Тогда (действия по порядку)**: создать задачу (ответственному по делу или тому, кто выполнил действие, срок в рабочих днях, приоритет); поставить процессуальный срок; запись в ленту; изменить статус дела; ИИ-шаг; вебхук во внешнюю систему.

## Подстановки
В текстах действий можно использовать поля события в двойных фигурных скобках: {{title}}, {{number}}, {{amount}}, {{at}}, {{party}} и другие - список показан под редактором правила.

## Шаблоны процессов
Готовые наборы правил устанавливаются одной кнопкой:
- **Взыскание**: претензия и срок ответа 30 дней при открытии дела; запись о платеже.
- **БФЛ**: запрос документов у клиента; подготовка к заседанию.
- **Конкурсное производство**: изучить новый акт и срок на обжалование 10 рабочих дней; задача по важному сообщению ЕФРСБ.
- **Судебный спор**: подготовка позиции к заседанию; при решении - срок на апелляцию (месяц) и задача оценить обжалование.
- **Абонентское обслуживание**: подписать договор при выигранной сделке; запись об оплате.

## Проверка и журнал
- **«Проверить»** - пробный запуск на примере данных: показывает, что сделает правило, ничего не создавая.
- **Журнал срабатываний**: время, правило, результат каждого действия, ошибки.
- Правило можно выключить, не удаляя. Создавать и менять правила может администратор.`,
  },

  // ---------------- Этап 3: аналитика, ИИ, коммуникации, мобильные ----------------
  {
    id: 'bi', section: 'Начало работы', title: 'Дашборды и конструктор отчётов', path: '/bi', module: 'M42',
    summary: 'Свои дашборды из виджетов: показатели, графики, таблицы по делам, задачам, платежам, долгам; вопрос на русском языке.',
    keywords: 'дашборд отчёт график аналитика bi виджет',
    body: `## Что можно построить
Виджет - это показатель, столбчатая или линейная диаграмма, круговая диаграмма или таблица по одному источнику: дела, задачи, платежи, счета, учёт времени, долги, исполнительные листы, сделки, судебные дела, затраты.

## Как создать дашборд
1. Раздел **Дашборды** → «Новый дашборд» или готовый набор.
2. «Добавить виджет»: источник, показатель (количество, сумма, среднее), группировка (по месяцу, сотруднику, стадии и другим полям), фильтры.
3. Или напишите вопрос обычными словами, например «поступления по месяцам за год»: ИИ предложит виджет, его можно поправить.

## Важно
Отчёты строятся только по данным, к которым у сотрудника есть доступ. Запросы выполняются по заранее описанным источникам, произвольный SQL не допускается.`,
  },
  {
    id: 'smartsearch', section: 'Документы и подпись', title: 'Поиск по смыслу и чат с делом', path: '/search', module: 'M31',
    summary: 'Поиск по тексту всех документов по смыслу, ответы на вопросы по материалам дела со ссылками на документы.',
    keywords: 'поиск смысл семантический вектор чат дело вопрос',
    body: `## Поиск по смыслу
Раздел **Поиск по смыслу**: запрос формулируется своими словами («кто поручитель по договору займа»). Находятся фрагменты документов, близкие по смыслу, даже без совпадения слов. Каждый результат открывает документ.

## Чат с делом
Вкладка **Чат с делом** в карточке дела (и «Чат» в карточке лица): вопросы по материалам. Ответ строится только по найденным фрагментам документов и карточке, после ответа указаны источники [1], [2] со ссылками.

## Индексация
Распознанные документы индексируются автоматически раз в минуту. Состояние индекса и переиндексация - кнопкой на странице поиска.`,
  },
  {
    id: 'scoring', section: 'Юридический контур', title: 'Оценка вероятности взыскания', path: '/debts', module: 'M19',
    summary: 'Оценка долга в процентах с факторами, ожидаемые поступления, стратегия; платёжная дисциплина клиента.',
    keywords: 'скоринг оценка вероятность взыскание стратегия портфель',
    body: `## Оценка долга
В карточке долга блок **Вероятность взыскания** → «Оценить». Учитываются: вид должника, сумма и срок просрочки, исковая давность, оплаты и выполнение обещаний, статус в ЕГРЮЛ, признаки банкротства, финансовая отчётность (выручка, чистые активы, прибыль), другие долги и иски к должнику, возвращённые исполнительные листы.

Результат: процент, ожидаемая сумма, рекомендуемая стратегия (досудебно, мировое, суд, банкротство, цессия, списание) и список факторов с вкладом каждого. «С комментарием ИИ» добавляет пояснение и шаги.

## Портфель
Раздел **Аналитика взыскания** → «Оценить»: пересчёт всех открытых долгов (или одного реестра), сумма к взысканию и ожидаемые поступления.

## Клиенты
В карточке клиента показывается платёжная дисциплина по выставленным счетам.

> Оценка справочная, решение принимает юрист.`,
  },
  {
    id: 'collection', section: 'Юридический контур', title: 'Работа с должниками и 230-ФЗ', path: '/collection', module: 'M23',
    summary: 'Журнал контактов, проверка частоты и времени по 230-ФЗ, анализ разговоров, обещания оплаты, отчёт по операторам.',
    keywords: '230-фз взыскание звонок должник оператор обещание контакт',
    body: `## Журнал контактов
В карточке долга блок **Контакты с должником** → «Добавить контакт»: способ, направление, текст разговора или переписки, обещание оплаты (сумма и дата).

## Контроль 230-ФЗ
Для должника-гражданина перед контактом проверяются ограничения ст. 7 230-ФЗ: звонки не чаще 1 в сутки, 2 в неделю, 8 в месяц; сообщения не чаще 2 в сутки, 4 в неделю, 16 в месяц; личные встречи не чаще 1 в неделю; время с 8 до 22 часов в будни и с 9 до 20 в выходные по местному времени должника. При нарушении контакт не сохраняется; уже состоявшийся контакт можно зафиксировать с отметкой о нарушении, она попадает в журнал аудита. Текст проверяется на угрозы и давление.

## Анализ и стратегия
- «Проанализировать» - ИИ определяет тональность, обещания, ключевые фразы и соблюдение скрипта.
- Лучшее время звонка считается по истории ответов; «Стратегия ИИ» составляет план действий.
- Обещание отмечается как исполненное или нарушенное, это учитывается в оценке долга.

## Отчёт
Раздел **Аналитика взыскания**: контакты, нарушения, обещания и выполнение скрипта по каждому оператору, список проблемных диалогов.`,
  },
  {
    id: 'enforcement', section: 'Юридический контур', title: 'Подсказки по исполнительному производству', path: '/writs', module: 'M24',
    summary: 'Следующее действие по 229-ФЗ, прогноз срока и доли взыскания, черновики заявлений, ходатайств и жалоб.',
    keywords: '229-фз пристав фссп жалоба ходатайство розыск арест выезд',
    body: `## Что делать дальше
В карточке исполнительного документа блок **Что сделать дальше** показывает действия с обоснованием: предъявить в ФССП или банк, срочно предъявить при истечении срока, запросить сведения о возбуждении, ходатайство об аресте, ограничение выезда (от 30 000 руб.), розыск (от 10 000 руб.), жалоба на бездействие, повторное предъявление, заявить требование в деле о банкротстве или ликвидационной комиссии (по данным мониторинга).

## Черновики
Кнопка «Черновик» готовит документ DOCX с данными листа, взыскателя и должника; недостающие сведения отмечены [____]. Черновик сохраняется в документах с тегом «черновик», его нужно проверить перед подачей.

## Прогноз
Ожидаемый срок и доля взыскания считаются по вашим завершённым производствам с тем же видом должника. При малой выборке прогноз помечен как ориентировочный.`,
  },
  {
    id: 'practice', section: 'Юридический контур', title: 'Судебная практика', path: '/practice', module: 'M21',
    summary: 'Статистика исходов по судьям, судам и категориям; обзоры актов по критериям с цитатами; дайджест изменений.',
    keywords: 'практика судья статистика обзор акт критерий дайджест',
    body: `## Статистика
Строится по вашим судебным делам: в карточке судебного дела укажите категорию спора и итог (выиграно, проиграно, частично, мировое, прекращено). Раздел **Судебная практика** → «Статистика исходов» по судьям, судам или категориям с долей успеха и размером выборки.

## Обзор практики
«Новый обзор»: вопрос, поиск актов среди ваших документов (или выбор вручную), критерии «да/нет» (или их формулирует ИИ). По каждому акту отмечается, выполнен ли критерий, с дословной цитатой. Цитата сверяется с текстом акта; если дословно не найдена, отметка помечается знаком «?». Итог - таблица с числом актов по каждому критерию и вывод со ссылками на акты.

## Похожие акты
Вкладка **Агент и практика** в деле → «Найти»: документы, близкие по смыслу к описанию дела.

## Дайджест
Собирается из лент официальных источников, которые администратор укажет в data\\settings.env (PRACTICE_FEEDS), по вашим темам.`,
  },
  {
    id: 'agent', section: 'ИИ и автоматизация', title: 'ИИ-агент и сводка дня', path: '/agent', module: 'M32',
    summary: 'Команды текстом и голосом, план действий с подтверждением, утренняя сводка, проверка готовности дела.',
    keywords: 'агент голос команда сводка приоритеты готовность',
    body: `## Команды
Раздел **ИИ-агент**: напишите или скажите («Голосом»), что сделать: поставить задачу, назначить встречу, добавить заметку в дело, выставить счёт по невыставленному времени. Агент показывает план по шагам; ничего не выполняется без кнопки «Выполнить». Действия проверяются по правам сотрудника и записываются в журнал аудита. Голос распознаётся на вашем сервере.

## Сводка дня
На главной и на странице агента: заседания на 2 дня, сроки, просроченные задачи и задачи на сегодня, новые события в судах и важные события мониторинга. «Приоритеты» - порядок дел на день от ИИ.

## Готовность дела
Вкладка **Агент и практика** в деле: проверка клиента, договора, просроченных задач и сроков, документов, распознавания, судебных дел, чек-листа АУ и публикаций. «Проверить с ИИ» добавляет перечень того, чего не хватает.`,
  },
  {
    id: 'bot', section: 'Клиенты онлайн', title: 'Чат-бот на сайте и в кабинете', path: '/bot', module: 'M22',
    summary: 'Ответы посетителям по вашей базе знаний, сбор контакта, сделка и задача, передача сотруднику.',
    keywords: 'бот чат сайт виджет консультация лид',
    body: `## Как работает
Бот отвечает только по статьям вашей базы знаний (раздел **Чат-бот** → «База знаний», есть готовый шаблон). Когда посетитель оставляет телефон, создаются лицо, сделка в воронке и задача ответственному. Если посетитель просит человека или обращение эмоциональное, диалог передаётся сотруднику.

## Диалоги
Вкладка «Диалоги»: ожидающие сотрудника, ответ от имени сотрудника, возврат боту, закрытие.

## Установка на сайт
Вкладка «Настройки и виджет»: строку подключения вставьте на сайт перед закрывающим тегом body. CRM должна быть доступна из интернета по HTTPS. В кабинете клиента бот видит дела клиента.`,
  },
  {
    id: 'sms', section: 'Клиенты онлайн', title: 'SMS-информирование', path: '/sms', module: 'M25',
    summary: 'Разовые сообщения и рассылки, шаблоны с подстановками, согласия, 230-ФЗ, тихие часы, журнал доставки.',
    keywords: 'sms смс рассылка шаблон напоминание',
    body: `## Отправка
Кнопка **SMS** в карточке лица и долга, раздел **SMS** для рассылок по клиентам, по тегу или по должникам. Подстановки: {name}, {debt}, {hearing}, {case}, {court}. SMS из автоматизаций - действие «SMS».

## Ограничения
- Рекламные сообщения уходят только лицам с согласием на рассылку.
- Сообщения должникам-гражданам проверяются по ограничениям 230-ФЗ.
- В тихие часы (по умолчанию с 21 до 9 по Москве) сообщения ждут в очереди.
- Причина неотправки видна в журнале.

## Подключение
Провайдер (SMSC, SMS.ru, МТС Exolve) указывается в data\\settings.env (SMS_PROVIDER и ключи).`,
  },
  {
    id: 'calls', section: 'Клиенты онлайн', title: 'Звонки и телефония', path: '/calls', module: 'M33',
    summary: 'Журнал звонков из АТС, карточка звонящего, пропущенные в задачи, запись с расшифровкой, резюме и оценка качества.',
    keywords: 'телефония звонок атс mango zadarma запись пропущенный',
    body: `## Что делает
- При входящем звонке у сотрудника всплывает карточка: номер и найденное лицо.
- Пропущенный звонок превращается в задачу перезвонить.
- Запись разговора сохраняется в документы, расшифровывается на вашем сервере, ИИ составляет резюме, задачи и оценку разговора (приветствие, выяснение потребности, следующий шаг, вежливость).
- «Позвонить» соединяет внутренний номер сотрудника (указывается в профиле) с клиентом.

## Без АТС
«Внести звонок» с загрузкой аудио: расшифровка и резюме работают так же.

## Подключение АТС
Mango Office, Zadarma или другая АТС с уведомлениями: адрес для уведомлений показан внизу раздела **Звонки** (видит администратор). Ключи задаются в data\\settings.env.`,
  },
  {
    id: 'inbox', section: 'Клиенты онлайн', title: 'Входящие: почта и мессенджеры', path: '/inbox', module: 'M34',
    summary: 'Обращения из почты, MAX, ВКонтакте, чата на сайте и других каналов в одной ленте, ответственные, контроль времени ответа.',
    keywords: 'почта мессенджер max вконтакте входящие обращения омниканальность',
    body: `## Лента
Раздел **Входящие**: каждое обращение - отдельная переписка с каналом, клиентом, ответственным и сроком ответа. Клиент определяется по телефону или почте автоматически, его можно выбрать вручную. Новые обращения распределяются между выбранными сотрудниками по очереди.

## Ответ
Ответ уходит в тот же канал. Быстрые ответы, «Черновик ИИ» по переписке и карточке клиента, ответ на другом языке и перевод входящего сообщения.

## Каналы
Почта (IMAP и SMTP), MAX, ВКонтакте настраиваются в data\\settings.env. Авито и WhatsApp подключаются через сервис-посредник по адресу уведомлений. Telegram и WhatsApp - иностранные сервисы, по умолчанию выключены и включаются администратором.

## Контроль
Время на ответ задаётся в настройках; просроченные обращения подсвечены. Отчёт по скорости первого ответа по сотрудникам.`,
  },
  {
    id: 'mobile', section: 'Клиенты онлайн', title: 'Мобильные приложения', path: '/m', module: 'M26',
    summary: 'Приложение сотрудника /m и приложение для клиентов «Мобильный Юрист» /app: установка на телефон, возможности.',
    keywords: 'мобильное приложение телефон pwa юрист клиент офлайн',
    body: `## Приложение сотрудника
Откройте адрес CRM с добавлением /m в браузере телефона и выберите «Установить» или «На экран Домой». Вкладки:
- **Сегодня**: заседания и задачи, отметка выполнения, напоминания о заседаниях за 1,5 часа (пока приложение открыто).
- **В суде**: крупно время, номер дела, суд и зал ближайших заседаний.
- **Дела**: поиск дел; звёздочка сохраняет дело на телефоне для работы без интернета. Заметка в дело текстом или голосом.
- **Съёмка**: фото страниц камерой с загрузкой в дело, текст распознаётся автоматически.

## «Мобильный Юрист» для клиентов (M28)
Адрес CRM с /app. Вход по номеру телефона и коду из SMS с согласием на обработку данных. Возможности: ИИ-помощник по ситуации, справочник статей, конструктор документов (претензии и заявления в DOCX), заказы: вопрос юристу, проверка документа с оплатой через ЮKassa, представительство. Каждый первый вопрос создаёт сделку в воронке.

Заказы обрабатываются в разделе **Мобильный Юрист**: назначение юриста, статус, ответ, загрузка результата, стоимость проверки.`,
  },
  {
    id: 'yookassa', section: 'Клиенты онлайн', title: 'Подключение оплаты ЮKassa', path: '/app-orders', module: 'M28',
    summary: 'Тестовый и настоящий магазин, ключи в settings.env, уведомления об оплате, кассовые чеки, проверка.',
    keywords: 'юкасса юkassa оплата платёж чек 54-фз касса',
    body: `## Порядок
1. Зарегистрируйте тестовый магазин: yookassa.ru/joinups?createTestShop=true.
2. В кабинете ЮKassa: **Настройки → Магазин** - shopId; **Интеграция → Ключи API** - секретный ключ (показывается один раз).
3. В data\\settings.env: PUBLIC_URL=https://адрес-crm, YOOKASSA_SHOP_ID=..., YOOKASSA_SECRET=...
4. В кабинете ЮKassa: **Интеграция → HTTP-уведомления** - адрес https://адрес-crm/api/v1/public/app/payments/yookassa, событие payment.succeeded.
5. Проверьте оплату в приложении /app тестовой картой 5555 5555 5555 4444.
6. Подключите настоящий магазин (анкета, договор, проверка ЮKassa) и замените ключи на боевые.

## Кассовые чеки
Подключите «Чеки от ЮKassa» или свою онлайн-кассу в кабинете ЮKassa, затем в settings.env: YOOKASSA_RECEIPT=true, YOOKASSA_VAT_CODE (1 - без НДС), при необходимости YOOKASSA_TAX_SYSTEM. Ставку и систему налогообложения согласуйте с бухгалтером.

## Безопасность
CRM проверяет каждую оплату запросом к ЮKassa, поддельное уведомление не отметит заказ оплаченным. Возвраты делаются в кабинете ЮKassa.

Полная инструкция - в документации поставки (docs\\yookassa.md).`,
  },
  {
    id: 'branding', section: 'Администрирование', title: 'Фирменный стиль, «О программе», ИИ и данные', path: '/admin/branding',
    summary: 'Название, цвет и логотип организации; сведения о программе; передача данных во внешний ИИ; выгрузка и обезличивание данных лица.',
    keywords: 'бренд логотип цвет white label о программе ии данные 152-фз выгрузка обезличивание',
    body: `## Фирменный стиль (M27)
**Администрирование → Фирменный стиль**: название в интерфейсе, короткое название, заголовок кабинета клиента, название приложения для клиентов, цвет и логотип. Применяется в CRM, кабинете клиента, приложениях и иконке на телефоне без правки программы.

## О программе
Ссылка «О программе» под названием в меню: наименование KIN DIGITAL CRM+, версия, правообладатель, номера свидетельства и записи в реестре, перечень используемых компонентов с лицензиями.

## ИИ и данные
**Администрирование → ИИ и данные**: режим «Только локально» (ИИ работает с моделью на вашем сервере, обращения к внешним сервисам блокируются) или «Разрешить внешний ИИ». Отдельно включается скрытие телефонов, e-mail, СНИЛС, паспортов, номеров карт и счетов перед отправкой. Решение записывается в журнал аудита.

## Данные субъекта
В карточке лица: выгрузка всех сведений о лице (ст. 14 152-ФЗ) и обезличивание по требованию субъекта (необратимо, доступно администратору).

## Пароли
При первом запуске пароль администратора создаётся случайным и записывается в файл data\\ПЕРВЫЙ ВХОД.txt. При входе со стандартным или слабым паролем программа предупреждает и предлагает сменить его; пароль можно оставить, тогда напоминание появится при следующем входе, а отказ записывается в журнал аудита.`,
  },

  // ---------------- Администрирование ----------------
  {
    id: 'admin', section: 'Администрирование', title: 'Пользователи, роли, справочники, лицензия, аудит', path: '/admin/users',
    summary: 'Сотрудники и роли с правами, настраиваемые справочники и поля, модули лицензии, журнал действий.',
    keywords: 'пользователь роль права доступ справочник лицензия аудит журнал пароль',
    body: `## Пользователи и роли
Готовые роли: владелец (администратор), руководитель отдела, юрист, арбитражный управляющий, помощник АУ или юриста, менеджер продаж, оператор взыскания, бухгалтер. Права роли можно менять: чтение и изменение лиц, сделок, договоров, дел, документов, долгов, процедур, финансов, управление шаблонами и пользователями, доступ ко всем делам.

## Справочники
Виды документов, категории затрат, источники клиентов и другие списки, а также настраиваемые поля карточек.

## Лицензия
Какие модули подключены и до какой даты. Недоступные модули не показываются в меню.

## Журнал аудита
Кто, когда и что сделал: создание, изменение, удаление, скачивание документов, смена стадий, удаление подписей.

## Профиль
Смена пароля - в профиле (имя внизу меню). Пароль администратора при первом запуске создаётся случайным (файл data\\ПЕРВЫЙ ВХОД.txt), при слабом пароле программа предложит его сменить.`,
  },
];

export const articleById = (id: string) => ARTICLES.find((a) => a.id === id);
